import logger from "../utils/logger";
/**
 * src/utils/authSyncChannel.js
 * 
 * Production-ready Cross-Tab Authentication, Inactivity & Meeting Synchronization.
 * 
 * Responsibilities:
 * 1. BroadcastChannel ('synexos_auth_sync') with fallback to window storage events.
 * 2. Shared cross-tab inactivity tracking in localStorage ('synexos_last_activity').
 * 3. Shared active meeting registry in localStorage ('synexos_active_meetings') with lease heartbeats.
 * 4. Stale lease cleanup (auto-expires crashed/closed meeting tabs after 35 seconds).
 * 5. Multi-tab event broadcasting (AUTH_LOGIN, AUTH_LOGOUT, AUTH_TOKEN_UPDATED, ACTIVITY_UPDATED, MEETING_HEARTBEAT, MEETING_ENDED).
 */

export const AUTH_SYNC_CHANNEL_NAME = "synexos_auth_sync";
export const ACTIVITY_STORAGE_KEY = "synexos_last_activity";
export const ACTIVE_MEETINGS_STORAGE_KEY = "synexos_active_meetings";

export const MEETING_HEARTBEAT_INTERVAL_MS = 10000; // Heartbeat every 10s
export const MEETING_LEASE_EXPIRY_MS = 35000; // Stale threshold: 35s (survives 3 missed beats)

export const AUTH_SYNC_EVENTS = {
  AUTH_LOGIN: "AUTH_LOGIN",
  AUTH_LOGOUT: "AUTH_LOGOUT",
  AUTH_TOKEN_UPDATED: "AUTH_TOKEN_UPDATED",
  ACTIVITY_UPDATED: "ACTIVITY_UPDATED",
  MEETING_HEARTBEAT: "MEETING_HEARTBEAT",
  MEETING_ENDED: "MEETING_ENDED",
};

/** localStorage key used as the transport when BroadcastChannel is unavailable. */
export const AUTH_SYNC_FALLBACK_STORAGE_KEY = "synexos_auth_sync_event";

let channelInstance = null;
let channelInitAttempted = false;
let storageFallbackInstalled = false;
const channelListeners = new Set();

// Every message carries a unique id; ids already delivered in this tab are ignored, so a
// message can never be processed twice (e.g. local delivery + a transport echo).
const SEEN_LIMIT = 200;
const seenMessageIds = [];
const seenMessageIdSet = new Set();

function markSeen(id) {
  if (!id || seenMessageIdSet.has(id)) return false;
  seenMessageIdSet.add(id);
  seenMessageIds.push(id);
  if (seenMessageIds.length > SEEN_LIMIT) {
    seenMessageIdSet.delete(seenMessageIds.shift());
  }
  return true;
}

function newMessageId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function deliver(message) {
  if (!message || typeof message !== "object") return;
  // Messages without an id come from older code paths; deliver them as before.
  if (message.id && !markSeen(message.id)) return;
  channelListeners.forEach((listener) => {
    try {
      listener(message);
    } catch (err) {
      logger.error("[AuthSyncChannel] Error in listener:", err?.message || "Unknown error");
    }
  });
}

function installStorageFallback() {
  if (storageFallbackInstalled || typeof window === "undefined") return;
  storageFallbackInstalled = true;
  window.addEventListener("storage", (e) => {
    if (e.key !== AUTH_SYNC_FALLBACK_STORAGE_KEY || !e.newValue) return;
    try {
      deliver(JSON.parse(e.newValue));
    } catch {
      /* ignore malformed payloads */
    }
  });
}

/**
 * Obtain or initialize the shared BroadcastChannel instance.
 */
export function getAuthSyncChannel() {
  if (typeof window === "undefined") return null;
  if (!channelInstance && !channelInitAttempted) {
    channelInitAttempted = true;
    if (typeof window.BroadcastChannel === "function") {
      try {
        channelInstance = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
        channelInstance.onmessage = (event) => {
          if (event && event.data) deliver(event.data);
        };
        channelInstance.onmessageerror = (err) => {
          logger.warn("[AuthSyncChannel] Message error:", err?.message || "Unknown error");
        };
      } catch (e) {
        logger.warn("[AuthSyncChannel] Failed to create BroadcastChannel, falling back to storage events:", e?.message || "Unknown error");
        channelInstance = null;
      }
    }
  }
  // The storage transport is always listened to: a sender without BroadcastChannel can
  // still reach us, and id de-duplication makes double delivery impossible.
  installStorageFallback();
  return channelInstance;
}

/**
 * Subscribe to cross-tab auth sync events.
 * Returns an unsubscribe cleanup function.
 */
export function subscribeAuthSync(listener) {
  if (typeof listener !== "function") return () => {};
  channelListeners.add(listener);
  getAuthSyncChannel(); // ensure initialized

  return () => {
    channelListeners.delete(listener);
  };
}

/**
 * Broadcast an event payload to all other tabs.
 *
 * Transport: BroadcastChannel when available, otherwise a localStorage write observed via
 * `storage` events in other tabs. Listeners must treat a message as a prompt to re-read
 * shared state, never as the state itself (a message may arrive late or out of order).
 *
 * @param {object} [options]
 * @param {boolean} [options.local=false] Also deliver to listeners in THIS tab. BroadcastChannel
 *        never echoes to the sender, so the initiating tab otherwise relies on its own code
 *        paths to update; `local` lets it reach exactly the same state as every other tab.
 */
export function broadcastAuthSync(type, payload = {}, options = {}) {
  if (typeof window === "undefined") return;
  const message = {
    ...payload,
    type,
    id: newMessageId(),
    timestamp: Date.now(),
  };

  const channel = getAuthSyncChannel();
  if (channel) {
    try {
      channel.postMessage(message);
    } catch (e) {
      /* ignore channel send failures */
    }
  } else {
    try {
      localStorage.setItem(AUTH_SYNC_FALLBACK_STORAGE_KEY, JSON.stringify(message));
    } catch {
      /* ignore storage failures */
    }
  }

  if (options && options.local) {
    deliver(message);
  } else {
    markSeen(message.id);
  }
}

/** Test-only: reset module state between test cases. */
export function __resetAuthSyncForTests() {
  try {
    channelInstance?.close?.();
  } catch {
    /* ignore */
  }
  channelInstance = null;
  channelInitAttempted = false;
  channelListeners.clear();
  seenMessageIds.length = 0;
  seenMessageIdSet.clear();
}

/**
 * Record a user interaction timestamp in the shared localStorage and notify other tabs.
 */
export function recordSharedActivity(forced = false, lastWriteRef = { current: 0 }, throttleMs = 15000) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (forced || now - lastWriteRef.current >= throttleMs) {
    lastWriteRef.current = now;
    try {
      localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
      broadcastAuthSync(AUTH_SYNC_EVENTS.ACTIVITY_UPDATED, { lastActivity: now });
    } catch {
      /* ignore storage write failures */
    }
  }
}

/**
 * Get the latest shared user activity timestamp across all tabs.
 */
export function getSharedLastActivityTime() {
  if (typeof window === "undefined") return Date.now();
  try {
    const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (raw) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  } catch {
    /* fallback */
  }
  return Date.now();
}

/**
 * Safely parse the active meetings registry from localStorage.
 */
function readActiveMeetingsMap() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(ACTIVE_MEETINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Write the active meetings registry to localStorage.
 */
function writeActiveMeetingsMap(map) {
  if (typeof window === "undefined") return;
  try {
    if (!map || Object.keys(map).length === 0) {
      localStorage.removeItem(ACTIVE_MEETINGS_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_MEETINGS_STORAGE_KEY, JSON.stringify(map));
    }
  } catch {
    /* ignore */
  }
}

/**
 * Register or update the heartbeat lease for an active meeting in this tab.
 * @param {string} tabSessionId Unique ID for this tab's meeting session.
 * @param {string} meetingId The meeting identifier.
 */
export function registerMeetingHeartbeat(tabSessionId, meetingId) {
  if (typeof window === "undefined" || !tabSessionId || !meetingId) return;
  const now = Date.now();
  const map = readActiveMeetingsMap();
  map[tabSessionId] = {
    meetingId,
    startedAt: map[tabSessionId]?.startedAt || now,
    lastHeartbeat: now,
  };
  writeActiveMeetingsMap(map);
  broadcastAuthSync(AUTH_SYNC_EVENTS.MEETING_HEARTBEAT, { tabSessionId, meetingId, timestamp: now });
}

/**
 * Unregister a meeting session when a user leaves or the meeting disconnects.
 * @param {string} tabSessionId Unique ID for this tab's meeting session.
 */
export function unregisterMeeting(tabSessionId) {
  if (typeof window === "undefined" || !tabSessionId) return;
  const map = readActiveMeetingsMap();
  if (map[tabSessionId]) {
    delete map[tabSessionId];
    writeActiveMeetingsMap(map);
    broadcastAuthSync(AUTH_SYNC_EVENTS.MEETING_ENDED, { tabSessionId, timestamp: Date.now() });
  }
}

/**
 * Check if ANY active meeting lease exists across all open browser tabs.
 * Automatically prunes expired leases (from crashed/closed tabs older than MEETING_LEASE_EXPIRY_MS).
 * Returns true if at least one valid active meeting lease is found.
 */
export function hasActiveMeetingLease() {
  if (typeof window === "undefined") return false;
  const map = readActiveMeetingsMap();
  const now = Date.now();
  let hasValid = false;
  let mutated = false;

  for (const [tabSessionId, entry] of Object.entries(map)) {
    if (entry && Number.isFinite(entry.lastHeartbeat) && now - entry.lastHeartbeat <= MEETING_LEASE_EXPIRY_MS) {
      hasValid = true;
    } else {
      // Lease expired (crashed or abruptly closed tab)
      delete map[tabSessionId];
      mutated = true;
    }
  }

  if (mutated) {
    writeActiveMeetingsMap(map);
  }

  return hasValid;
}

/**
 * Clean up all meeting leases upon global user logout.
 */
export function clearAllMeetingLeases() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(ACTIVE_MEETINGS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
